/**
 * Settings → Sources (W3-01 sub-item, W3-02).
 *
 * Two lists:
 *  - Manifest sources (`GET /admin/sources`, super-admin): every row of
 *    services/sources/manifests/pipeline_sources.yaml with its status, cadence
 *    and the result of its last harvest_runs row.
 *  - Source candidates (`GET /admin/source-candidates`, reviewers): sources
 *    officers filed from cases, their lifecycle state, and a review form that
 *    walks the checklist. Approval (admins) shows the manifest stanza to paste;
 *    nothing is scheduled until the importer exists and a trial run is recorded
 *    (docs/SOURCE_ONBOARDING_LIFECYCLE.md).
 *
 * The backend enforces every gate; the props only decide what is rendered.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, ClipboardCopy, Database, Loader2, RefreshCw, XCircle } from 'lucide-react';
import {
    sourcesService,
    type ManifestSource,
    type ManifestSourcesResponse,
    type SourceCandidate,
    type SourceChecklistQuestion,
    type SourceReviewAnswers,
} from '../../services/api';
import { Button } from '../ui/button';
import { cn } from '../../utils/cn';

export interface SourcesTabProps {
    /** `admin.super`: may read the manifest and ledger. */
    canViewManifest: boolean;
    /** `scrapers`: may list candidates and save reviews. */
    canReview: boolean;
    /** `admin.tools`: may approve or reject candidates. */
    canApprove: boolean;
}

export const LIFECYCLE_LABELS: Record<string, string> = {
    proposed: 'Proposed',
    under_review: 'Under review',
    rejected: 'Rejected',
    awaiting_importer: 'Approved for build: awaiting importer',
    importer_built: 'Importer in manifest: no trial run yet',
    trial_run_recorded: 'Trial run recorded: awaiting schedule approval',
    approved_not_scheduled: 'Approved, not scheduled',
    scheduled: 'Scheduled',
    paused: 'Paused',
    retired: 'Retired',
};

function errorText(err: unknown): string {
    const e = err as { response?: { status?: number; data?: { detail?: unknown } }; message?: string };
    const detail = e?.response?.data?.detail as { code?: string; missing?: string[]; message?: string } | string | undefined;
    if (detail && typeof detail === 'object') {
        if (detail.code === 'review_incomplete' && detail.missing) return `Review incomplete: ${detail.missing.join(', ')}`;
        return detail.message || detail.code || 'Request failed';
    }
    if (typeof detail === 'string') return detail;
    return e?.message || 'Request failed';
}

function fmtTime(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
}

function statusTone(status: string | null | undefined): string {
    switch (status) {
        case 'success':
        case 'active':
        case 'scheduled':
            return 'text-emerald-600 dark:text-emerald-400';
        case 'failed':
        case 'rejected':
            return 'text-red-600 dark:text-red-400';
        case 'optional_failure':
        case 'partial':
        case 'paused':
            return 'text-amber-600 dark:text-amber-400';
        default:
            return 'text-muted-foreground';
    }
}

// ── Manifest ─────────────────────────────────────────────────────────────

const LEDGER_NOTE: Record<string, string> = {
    missing_table: 'No run ledger yet (no pipeline run since migration 027); run fields are empty.',
    unavailable: 'Database unavailable: run fields could not be read.',
    error: 'The run ledger could not be read: run fields are empty.',
};

export const ManifestSourcesList: React.FC = () => {
    const [data, setData] = useState<ManifestSourcesResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState('');
    const [nonce, setNonce] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        sourcesService.listSources()
            .then((d) => { if (!cancelled) { setData(d); setError(null); } })
            .catch((err) => { if (!cancelled) setError(errorText(err)); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [nonce]);

    const rows = useMemo(() => {
        const all = data?.sources || [];
        const q = filter.trim().toLowerCase();
        if (!q) return all;
        return all.filter((s) => [s.id, s.name, s.status, s.cadence, s.table, s.last_status || '']
            .some((v) => v.toLowerCase().includes(q)));
    }, [data, filter]);

    return (
        <section className="bg-card border border-border rounded-lg overflow-hidden" aria-label="Manifest sources">
            <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-b border-border">
                <div className="flex items-center gap-2">
                    <Database className="w-5 h-5 text-primary" />
                    <h3 className="font-medium">Manifest sources</h3>
                    {data && <span className="px-2 py-0.5 text-xs bg-secondary rounded-full">{data.sources.length}</span>}
                </div>
                <div className="flex items-center gap-2">
                    <input
                        aria-label="Filter sources"
                        placeholder="Filter by id, status, table"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="h-8 w-56 rounded-md border border-input bg-background px-2 text-xs"
                    />
                    <Button size="sm" variant="outline" onClick={() => setNonce((n) => n + 1)} disabled={loading}>
                        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                        <span className="ml-1">Refresh</span>
                    </Button>
                </div>
            </div>
            {error && <p className="p-4 text-sm text-red-600 dark:text-red-400" role="alert">Failed to load sources: {error}</p>}
            {data && data.ledger !== 'ok' && (
                <p className="px-4 pt-3 text-xs text-amber-600 dark:text-amber-400">{LEDGER_NOTE[data.ledger] || `Ledger: ${data.ledger}`}</p>
            )}
            {data && (
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="text-left text-muted-foreground">
                            <tr>
                                <th className="px-4 py-2 font-medium">Source</th>
                                <th className="px-2 py-2 font-medium">Status</th>
                                <th className="px-2 py-2 font-medium">Cadence</th>
                                <th className="px-2 py-2 font-medium">Table</th>
                                <th className="px-2 py-2 font-medium">Last run</th>
                                <th className="px-2 py-2 font-medium">Result</th>
                                <th className="px-2 py-2 font-medium text-right">Written / withheld</th>
                                <th className="px-4 py-2 font-medium">Next run</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((s: ManifestSource) => (
                                <tr key={s.id} className="border-t border-border" data-testid={`manifest-source-${s.id}`}>
                                    <td className="px-4 py-2">
                                        <div className="font-medium text-foreground">{s.name}</div>
                                        <div className="font-mono text-[11px] text-muted-foreground">{s.id}</div>
                                    </td>
                                    <td className={cn('px-2 py-2', statusTone(s.status))}>{s.status}</td>
                                    <td className="px-2 py-2">{s.cadence}</td>
                                    <td className="px-2 py-2 font-mono">{s.table}</td>
                                    <td className="px-2 py-2">{fmtTime(s.last_run_at)}</td>
                                    <td className={cn('px-2 py-2', statusTone(s.last_status))}>{s.last_status || 'never ran'}</td>
                                    <td className="px-2 py-2 text-right tabular-nums">
                                        {s.last_rows_written ?? '—'} / {s.last_rows_withheld ?? '—'}
                                    </td>
                                    <td className="px-4 py-2">{s.runs ? fmtTime(s.next_run_at) : 'not scheduled'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};

// ── Candidate review form ────────────────────────────────────────────────

function answerToText(v: string | string[] | undefined): string {
    if (Array.isArray(v)) return v.join('\n');
    return v || '';
}

function isBlank(v: string | undefined): boolean {
    const t = (v || '').trim();
    return !t || t.toLowerCase() === 'unknown';
}

export const CandidateReviewForm: React.FC<{
    candidate: SourceCandidate;
    checklist: SourceChecklistQuestion[];
    canApprove: boolean;
    onChanged: (c: SourceCandidate) => void;
}> = ({ candidate, checklist, canApprove, onChanged }) => {
    const [draft, setDraft] = useState<Record<string, string>>(() => {
        const out: Record<string, string> = {};
        for (const q of checklist) out[q.key] = answerToText(candidate.review_answers?.[q.key]);
        return out;
    });
    const [busy, setBusy] = useState<'save' | 'approve' | 'reject' | null>(null);
    const [rejectReason, setRejectReason] = useState('');
    const open = candidate.status === 'proposed' || candidate.status === 'under_review';

    const missing = useMemo(
        () => checklist.filter((q) => q.required && isBlank(draft[q.key])).map((q) => q.key),
        [checklist, draft],
    );
    const savedMissing = candidate.missing_answers || [];
    const dirty = checklist.some((q) => draft[q.key] !== answerToText(candidate.review_answers?.[q.key]));
    const canApproveNow = open && candidate.status === 'under_review' && savedMissing.length === 0 && !dirty;

    const sections = useMemo(() => {
        const out: { name: string; questions: SourceChecklistQuestion[] }[] = [];
        for (const q of checklist) {
            const last = out[out.length - 1];
            if (last && last.name === q.section) last.questions.push(q);
            else out.push({ name: q.section, questions: [q] });
        }
        return out;
    }, [checklist]);

    const save = async () => {
        const answers: SourceReviewAnswers = {};
        for (const q of checklist) {
            const v = draft[q.key] ?? '';
            answers[q.key] = q.kind === 'list' ? v.split('\n').map((l) => l.trim()).filter(Boolean) : v;
        }
        setBusy('save');
        try {
            const updated = await sourcesService.review(candidate.id, answers);
            onChanged(updated);
            toast.success(updated.missing_answers.length ? `Review saved; ${updated.missing_answers.length} required answers left` : 'Review saved; ready for approval');
        } catch (err) {
            toast.error(`Could not save the review: ${errorText(err)}`);
        } finally {
            setBusy(null);
        }
    };

    const approve = async () => {
        setBusy('approve');
        try {
            const updated = await sourcesService.approve(candidate.id);
            onChanged(updated);
            toast.success('Approved for build. Copy the manifest stanza below.');
        } catch (err) {
            toast.error(`Could not approve: ${errorText(err)}`);
        } finally {
            setBusy(null);
        }
    };

    const reject = async () => {
        if (!rejectReason.trim()) return;
        setBusy('reject');
        try {
            const updated = await sourcesService.reject(candidate.id, rejectReason.trim());
            onChanged(updated);
            toast.success('Candidate rejected');
        } catch (err) {
            toast.error(`Could not reject: ${errorText(err)}`);
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="space-y-4 p-4 bg-muted/30 border-t border-border" data-testid="candidate-review-form">
            {sections.map((section) => (
                <fieldset key={section.name} className="space-y-3" disabled={!open}>
                    <legend className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{section.name}</legend>
                    {section.questions.map((q) => {
                        const id = `q-${candidate.id}-${q.key}`;
                        const value = draft[q.key] ?? '';
                        const set = (v: string) => setDraft((d) => ({ ...d, [q.key]: v }));
                        return (
                            <div key={q.key} className="space-y-1">
                                <label htmlFor={id} className="block text-sm font-medium">
                                    {q.label}{q.required && <span className="text-red-600" aria-hidden="true"> *</span>}
                                    {q.required && isBlank(value) && <span className="ml-2 text-xs font-normal text-amber-600">unanswered</span>}
                                </label>
                                <p className="text-xs text-muted-foreground">{q.help}</p>
                                {q.kind === 'enum' ? (
                                    <select id={id} value={value} onChange={(e) => set(e.target.value)}
                                        className="h-8 w-full max-w-sm rounded-md border border-input bg-background px-2 text-sm">
                                        <option value="">Choose…</option>
                                        {q.choices.map((c) => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                ) : q.kind === 'list' || ['what_it_is', 'legal_basis', 'usage', 'kill_switch', 'notes'].includes(q.key) ? (
                                    <textarea id={id} value={value} onChange={(e) => set(e.target.value)} rows={3}
                                        className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm" />
                                ) : (
                                    <input id={id} value={value} onChange={(e) => set(e.target.value)}
                                        className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm" />
                                )}
                            </div>
                        );
                    })}
                </fieldset>
            ))}

            {open && (
                <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border">
                    <Button size="sm" onClick={save} disabled={busy !== null || !dirty}>
                        {busy === 'save' && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}Save review
                    </Button>
                    {canApprove && (
                        <Button size="sm" variant="secondary" onClick={approve} disabled={busy !== null || !canApproveNow}
                            title={canApproveNow ? 'Approve for build' : 'Answer and save every required question first'}>
                            {busy === 'approve' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                            Approve for build
                        </Button>
                    )}
                    <span className="text-xs text-muted-foreground" data-testid="review-missing">
                        {missing.length ? `${missing.length} required answers missing` : dirty ? 'All required answers given; save to enable approval' : 'All required answers saved'}
                    </span>
                    {canApprove && (
                        <div className="flex items-center gap-2 ml-auto">
                            <input aria-label="Reject reason" placeholder="Reason to reject" value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                className="h-8 w-56 rounded-md border border-input bg-background px-2 text-xs" />
                            <Button size="sm" variant="outline" onClick={reject} disabled={busy !== null || !rejectReason.trim()}>
                                <XCircle className="w-3.5 h-3.5 mr-1" />Reject
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ── Candidates list ──────────────────────────────────────────────────────

const StanzaBlock: React.FC<{ candidate: SourceCandidate }> = ({ candidate }) => {
    if (!candidate.manifest_stanza) return null;
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(candidate.manifest_stanza || '');
            toast.success('Stanza copied');
        } catch {
            toast.error('Copy failed; select the text instead');
        }
    };
    return (
        <div className="p-4 border-t border-border space-y-2" data-testid="manifest-stanza">
            <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                    Paste under <code>sources:</code> in <code>backend/app/services/sources/manifests/pipeline_sources.yaml</code> in
                    the same commit as the importer. It stays <code>status: proposed</code> and never runs until a trial run is in
                    the ledger and an admin sets it active.
                </p>
                <Button size="sm" variant="outline" onClick={copy}><ClipboardCopy className="w-3.5 h-3.5 mr-1" />Copy</Button>
            </div>
            <pre className="text-[11px] leading-snug bg-background border border-border rounded p-2 overflow-x-auto">{candidate.manifest_stanza}</pre>
        </div>
    );
};

export const SourceCandidatesList: React.FC<{ canApprove: boolean }> = ({ canApprove }) => {
    const [candidates, setCandidates] = useState<SourceCandidate[]>([]);
    const [checklist, setChecklist] = useState<SourceChecklistQuestion[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [openId, setOpenId] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [nonce, setNonce] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        sourcesService.listCandidates(statusFilter ? { status: statusFilter } : undefined)
            .then((d) => {
                if (cancelled) return;
                setCandidates(d.candidates || []);
                setChecklist(d.checklist || []);
                setError(null);
            })
            .catch((err) => { if (!cancelled) setError(errorText(err)); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [statusFilter, nonce]);

    const onChanged = useCallback((updated: SourceCandidate) => {
        setCandidates((list) => list.map((c) => (c.id === updated.id
            ? { ...c, ...updated, lifecycle_state: updated.status === 'approved' ? 'awaiting_importer' : updated.status }
            : c)));
    }, []);

    return (
        <section className="bg-card border border-border rounded-lg overflow-hidden" aria-label="Source candidates">
            <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-b border-border">
                <div className="flex items-center gap-2">
                    <h3 className="font-medium">Source candidates</h3>
                    <span className="px-2 py-0.5 text-xs bg-secondary rounded-full">{candidates.length}</span>
                </div>
                <div className="flex items-center gap-2">
                    <select aria-label="Candidate status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                        className="h-8 rounded-md border border-input bg-background px-2 text-xs">
                        <option value="">All states</option>
                        <option value="proposed">Proposed</option>
                        <option value="under_review">Under review</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                    </select>
                    <Button size="sm" variant="outline" onClick={() => setNonce((n) => n + 1)} disabled={loading}>
                        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                        <span className="ml-1">Refresh</span>
                    </Button>
                </div>
            </div>
            <p className="px-4 pt-3 text-xs text-muted-foreground">
                Officers file candidates from a case page (Propose a source). A reviewer answers the checklist; an admin approves
                it for build, which produces the manifest stanza. Nothing is scheduled until the importer exists and a trial run
                is recorded.
            </p>
            {error && <p className="p-4 text-sm text-red-600 dark:text-red-400" role="alert">Failed to load candidates: {error}</p>}
            {!error && !loading && candidates.length === 0 && (
                <p className="p-4 text-sm text-muted-foreground">No candidates{statusFilter ? ' in this state' : ''}.</p>
            )}
            <ul className="divide-y divide-border mt-2">
                {candidates.map((c) => {
                    const state = c.lifecycle_state || c.status;
                    return (
                        <li key={c.id} data-testid={`candidate-${c.id}`}>
                            <div className="flex flex-wrap items-start justify-between gap-3 p-4">
                                <div className="min-w-0 space-y-1">
                                    <div className="font-mono text-xs break-all">{c.url}</div>
                                    <div className="text-xs text-muted-foreground">
                                        hash <span className="font-mono" title={c.url_hash}>{c.url_hash.slice(0, 12)}…</span>
                                        {' · '}filed by {c.proposed_by_name || 'unknown'} on {fmtTime(c.created_at)}
                                        {c.case_id && <> · case <a className="underline" href={`/cases/${c.case_id}`}>{c.case_id.slice(0, 8)}</a></>}
                                    </div>
                                    <div className="text-sm">{c.what_it_showed}</div>
                                    <div className="text-xs"><span className="text-muted-foreground">Legal note: </span>{c.legal_note || 'none given'}</div>
                                    {c.reject_reason && <div className="text-xs text-red-600">Rejected: {c.reject_reason}</div>}
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    <span className={cn('text-xs font-medium', statusTone(state))} data-testid="candidate-state">
                                        {LIFECYCLE_LABELS[state] || state}
                                    </span>
                                    {c.approved_manifest_id && <span className="font-mono text-[11px] text-muted-foreground">{c.approved_manifest_id}</span>}
                                    <Button size="sm" variant="outline" onClick={() => setOpenId(openId === c.id ? null : c.id)}>
                                        {openId === c.id ? 'Close' : (c.status === 'proposed' || c.status === 'under_review') ? 'Review' : 'View'}
                                    </Button>
                                </div>
                            </div>
                            {openId === c.id && checklist.length > 0 && (
                                <CandidateReviewForm key={`${c.id}-${c.updated_at}`} candidate={c} checklist={checklist}
                                    canApprove={canApprove} onChanged={onChanged} />
                            )}
                            {openId === c.id && <StanzaBlock candidate={c} />}
                        </li>
                    );
                })}
            </ul>
        </section>
    );
};

export const SourcesTab: React.FC<SourcesTabProps> = ({ canViewManifest, canReview, canApprove }) => (
    <div className="space-y-6">
        {canViewManifest && <ManifestSourcesList />}
        {canReview && <SourceCandidatesList canApprove={canApprove} />}
        {!canViewManifest && !canReview && (
            <p className="text-sm text-muted-foreground">You do not have access to data sources.</p>
        )}
    </div>
);

export default SourcesTab;
